import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar,
  Stack, Divider, Tooltip, Menu, MenuItem, Chip, Button, CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FitnessCenter as GymIcon,
  SportsGymnastics as ExerciseIcon,
  Notifications as NotifIcon,
  Groups as BatchIcon,
  Logout as LogoutIcon,
  CalendarMonth as PlanIcon,
  EmojiEvents as ChallengesIcon,
  Restaurant as MealIcon,
  AdminPanelSettings as StaffIcon,
  QrCode2 as AttendanceIcon,
  Storage as StorageIcon,
  Feedback as FeedbackIcon,
  SetMeal as FoodIcon,
  CreditCard as PaymentsIcon,
  AccountBalanceWallet as UpiIcon,
  Schedule as SlotIcon,
  QuestionAnswer as TrainerIcon,
  Psychology as StressIcon,
  SelfImprovement as YogaIcon,
  Settings as SettingsIcon,
  TrendingDown as TrendingDownIcon,
  KeyboardArrowDown,
  LightMode,
  DarkMode,
  Download as DownloadIcon,
  PersonAddAlt1 as LeadsIcon,
  Message, ViewCarousel, Cable as IntegrationsIcon, PointOfSale as POSIcon,
  Share as SocialIcon, Fingerprint as FingerprintIcon
} from '@mui/icons-material';

import { Badge } from '@mui/material';
import { notificationsAPI } from '../services/api';
import { useAppData } from '../context/AppDataContext';
import { ColorModeContext } from '../context/ThemeContext';
import { GYM_MODULES, getId, userCanAccessGymModule } from '../utils/gymModules';

const DRAWER_W = 264;


// ── Role badge ────────────────────────────────────────────────────────────────
const ROLES = {
  superadmin: { label: 'Super Admin', color: '#7C3AED', bg: '#F5F3FF' },
  gym_admin:  { label: 'Gym Admin',   color: '#0891B2', bg: '#ECFEFF' },
  admin:      { label: 'Admin',       color: '#059669', bg: '#ECFDF5' },
  trainer:    { label: 'Trainer',     color: '#D97706', bg: '#FFFBEB' },
  manager:    { label: 'Manager',     color: '#0F766E', bg: '#ECFDF5' },
  coach:      { label: 'Coach',       color: '#1D4ED8', bg: '#EFF6FF' },
  sales_staff:{ label: 'Sales',       color: '#C2410C', bg: '#FFEDD5' },
  front_desk: { label: 'Front Desk',  color: '#0EA5E9', bg: '#EFF6FF' },
};

// ── Nav items ─────────────────────────────────────────────────────────────────
const superadminItems = [
  { divider: true, label: 'Most Used' },
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Communication', icon: <Message />,       path: '/dashboard/marketing' },
  
  { divider: true, label: 'Manage' },
  { text: 'Gyms',          icon: <GymIcon />,       path: '/dashboard/gyms' },
  { text: 'Staff',         icon: <StaffIcon />,     path: '/dashboard/staff' },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { text: 'Banners',       icon: <ViewCarousel />,  path: '/dashboard/banners' },
  
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers' },
  { text: 'Yoga Content',  icon: <YogaIcon />,      path: '/dashboard/yoga-content' },
  { text: 'Stress Content', icon: <StressIcon />,    path: '/dashboard/stress-content' },
  
  { divider: true, label: 'Operations' },
  { text: 'Social Operations', icon: <SocialIcon />, path: '/dashboard/social-operations' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management' },
  { text: 'Challenges & Events', icon: <ChallengesIcon />, path: '/dashboard/challenges-events' },
  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention' },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },

  
  { divider: true, label: 'Payments & POS' },
  { text: 'Payments',      icon: <PaymentsIcon />,  path: '/dashboard/payments' },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Gym Invoices',  icon: <UpiIcon />,       path: '/dashboard/gym-invoices' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices' },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings' },
  
  { divider: true, label: 'Settings & Data' },
  { text: 'Notification Templates', icon: <NotifIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notification-templates' },
  { text: 'Integrations',  icon: <IntegrationsIcon />, path: '/dashboard/integrations' },
  { text: 'Database',      icon: <StorageIcon />,   path: '/dashboard/database' },
];

const gymAdminItems = (gymId) => [
  { divider: true, label: 'Most Used' },
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Communication', icon: <Message />,       path: '/dashboard/marketing' },
  
  { divider: true, label: 'Manage' },
  { text: 'My Gym',        icon: <GymIcon />,       path: `/dashboard/gyms/${gymId}` },
  { text: 'Staff',         icon: <StaffIcon />,     path: '/dashboard/staff', module: GYM_MODULES.trainerStaff },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { text: 'Banners',       icon: <ViewCarousel />,  path: '/dashboard/banners' },
  
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers', module: GYM_MODULES.trainerStaff },
  { text: 'Yoga Content',  icon: <YogaIcon />,      path: '/dashboard/yoga-content', defaultGymOnly: true },
  { text: 'Stress Content', icon: <StressIcon />,    path: '/dashboard/stress-content', defaultGymOnly: true },
  
  { divider: true, label: 'Operations' },
  { text: 'Social Operations', icon: <SocialIcon />, path: '/dashboard/social-operations' },
  { text: 'Biometric & Hardware', icon: <FingerprintIcon />, path: '/dashboard/biometric' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management', module: GYM_MODULES.slotBooking },
  { text: 'Challenges & Events', icon: <ChallengesIcon />, path: '/dashboard/challenges-events' },
  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention', module: GYM_MODULES.memberRetention },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },
  
  { divider: true, label: 'Payments & POS' },
  { text: 'UPI Payments',  icon: <UpiIcon />,       path: '/dashboard/gym-upi-payments', module: GYM_MODULES.invoice },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices', module: GYM_MODULES.invoice },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings', module: GYM_MODULES.invoice },
  
  { divider: true, label: 'Settings' },
  { text: 'Gym Settings',  icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/gym-settings' },
  { text: 'Notification Templates', icon: <NotifIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notification-templates' },
  { text: 'Integrations',  icon: <IntegrationsIcon />, path: '/dashboard/integrations' },
];

const adminItems = [
  { divider: true, label: 'Most Used' },
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Communication', icon: <Message />,       path: '/dashboard/marketing' },
  
  { divider: true, label: 'Manage' },
  { text: 'Staff',         icon: <StaffIcon />,     path: '/dashboard/staff', module: GYM_MODULES.trainerStaff },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { text: 'Banners',       icon: <ViewCarousel />,  path: '/dashboard/banners' },
  
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers', module: GYM_MODULES.trainerStaff },
  
  { divider: true, label: 'Operations' },
  { text: 'Social Operations', icon: <SocialIcon />, path: '/dashboard/social-operations' },
  { text: 'Biometric & Hardware', icon: <FingerprintIcon />, path: '/dashboard/biometric' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management', module: GYM_MODULES.slotBooking },
  { text: 'Challenges & Events', icon: <ChallengesIcon />, path: '/dashboard/challenges-events' },

  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention', module: GYM_MODULES.memberRetention },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },
  
  { divider: true, label: 'Payments & POS' },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices', module: GYM_MODULES.invoice },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings', module: GYM_MODULES.invoice },
  
  { divider: true, label: 'Settings' },
  { text: 'Gym Settings',  icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/gym-settings' },
  { text: 'Notification Templates', icon: <NotifIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notification-templates' },
  { text: 'Integrations',  icon: <IntegrationsIcon />, path: '/dashboard/integrations' },
];

const managerItems = adminItems.filter(item => item.text !== 'Challenges & Events');

const coachItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers', module: GYM_MODULES.trainerStaff },
];

const salesStaffItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { divider: true, label: 'Communication' },
  { text: 'Communication',     icon: <Message />,       path: '/dashboard/marketing' },
  { divider: true, label: 'Operations' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention', module: GYM_MODULES.memberRetention },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices', module: GYM_MODULES.invoice },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings', module: GYM_MODULES.invoice },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },
];

const frontDeskItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { divider: true, label: 'Operations' },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management', module: GYM_MODULES.slotBooking },
];

function cleanDividers(items) {
  return items.filter((item, index, arr) => {
    if (!item.divider) return true;
    const next = arr[index + 1];
    return next && !next.divider;
  });
}

function filterModuleItems(items, user, gyms) {
  return cleanDividers(items.filter(item => {
    if (item.divider) return true;
    if (item.defaultGymOnly) {
      const defaultGym = gyms.find(g => g.isDefault);
      if (user?.role !== 'admin' || !defaultGym || getId(user.gymId) !== getId(defaultGym)) return false;
    }
    return userCanAccessGymModule(user, gyms, item.module);
  }));
}

function getMenuItems(user, gyms) {
  if (!user) return superadminItems;
  if (user.role === 'superadmin') return superadminItems;
  if (user.role === 'gym_admin') return filterModuleItems(gymAdminItems(getId(user.gymId)), user, gyms);
  if (user.role === 'manager') return filterModuleItems(managerItems, user, gyms);
  if (user.role === 'coach') return filterModuleItems(coachItems, user, gyms);
  if (user.role === 'sales_staff') return filterModuleItems(salesStaffItems, user, gyms);
  if (user.role === 'front_desk') return filterModuleItems(frontDeskItems, user, gyms);
  return filterModuleItems(adminItems, user, gyms);
}

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name[0].toUpperCase();
}

function isActive(item, pathname) {
  if (item.path === '/dashboard') return pathname === '/dashboard';
  return pathname.startsWith(item.path);
}

// ── Sidebar content ───────────────────────────────────────────────────────────
function SidebarContent({ items, location, navigate, onClose, tokens: T }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: T.sidebarBg }}>
      {/* Nav */}
      <List sx={{ px: 1.25, py: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        {items.map((item, idx) => {
          if (item.divider) {
            return (
              <Box key={idx} sx={{ px: 1.5, pt: idx === 0 ? 0 : 2, pb: 0.75 }}>
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: T.sidebarTextLight, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {item.label}
                </Typography>
              </Box>
            );
          }
          const active = isActive(item, location.pathname);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); onClose?.(); }}
                sx={{
                  borderRadius: 1,
                  py: 1,
                  px: 1.5,
                  minHeight: 40,
                  bgcolor: active ? T.sidebarActiveBg : 'transparent',
                  border: `1px solid ${active ? 'rgba(37, 99, 235, 0.2)' : 'transparent'}`,
                  '&:hover': { bgcolor: active ? T.sidebarActiveBg : T.sidebarHoverBg },
                  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: active ? T.primary : T.sidebarTextMid }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 18 } })}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 500,
                    color: active ? T.sidebarTextActive : T.sidebarTextMid,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

    </Box>
  );
}

// ── Main layout ───────────────────────────────────────────────────────────────
export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl]     = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const { gyms } = useAppData();
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = getUser();
  const items     = getMenuItems(user, gyms);
  const role      = ROLES[user?.role] || ROLES.admin;

  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const isDark = theme.palette.mode === 'dark';

  const loadNotifications = useCallback(async () => {
    try {
      setNotifLoading(true);
      const response = await notificationsAPI.getMy({ page: 1, limit: 10 });
      const items = response.data?.data || [];
      setNotifications(items);
      setNotifUnreadCount(items.filter(item => !item.isRead).length);
    } catch (err) {
      console.error('Failed to load recent notifications', err);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleOpenNotifications = (event) => {
    setNotifAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleCloseNotifications = () => {
    setNotifAnchorEl(null);
  };

  const T = {
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    primaryBg: isDark ? '#1E3A8A' : '#EFF6FF',
    bg: theme.palette.background.default,
    surface: theme.palette.background.paper,
    surfaceAlt: isDark ? '#334155' : '#F1F5F9',
    border: theme.palette.divider,
    textDark: theme.palette.text.primary,
    textMid: theme.palette.text.secondary,
    textLight: theme.palette.text.disabled,
    red: theme.palette.error.main,
    sidebarBg: isDark ? '#0F172A' : '#FFFFFF',
    sidebarBorder: theme.palette.divider,
    sidebarTextActive: isDark ? '#F8FAFC' : '#0F172A',
    sidebarTextMid: isDark ? '#CBD5E1' : '#475569',
    sidebarTextLight: isDark ? '#94A3B8' : '#64748B',
    sidebarHoverBg: isDark ? '#1E293B' : '#F8FAFC',
    sidebarActiveBg: isDark ? '#1E3A8A' : '#EFF6FF',
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: T.bg }}>

      {/* ── Top AppBar ── */}
      <AppBar position="fixed" elevation={0} sx={{
        zIndex: t => t.zIndex.drawer + 1,
        bgcolor: T.surface,
        borderBottom: `1px solid ${T.border}`,
      }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important', px: { xs: 1.5, sm: 2.5, lg: 3 } }}>

          {/* Left: logo + hamburger (mobile) + page title */}
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {/* Logo — always visible */}
            

            {/* Divider between logo and page title — desktop only */}
            {/* Hamburger — mobile only */}
            <IconButton size="small" onClick={() => setMobileOpen(v => !v)}
              sx={{ display: { sm: 'none' }, color: T.textMid, borderRadius: 1, border: `1px solid ${T.border}`, width: 36, height: 36 }}>
              <MenuIcon fontSize="small" />
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 1, border: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
                bgcolor: T.surface, boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
              }}>
                <img src="/logo.png" alt="Fitistic" style={{ width: 28, height: 28, objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              </Box>
              <Typography fontWeight={700} sx={{ fontSize: '1.125rem', color: T.textDark, letterSpacing: '-0.01em', display: { xs: 'none', sm: 'block' } }}>
                Fitistic
              </Typography>
              <Box sx={{ px: 1, py: 0.25, borderRadius: 0.5, bgcolor: T.primaryBg, border: `1px solid rgba(37,99,235,0.2)`, display: { xs: 'none', sm: 'block' } }}>
                <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: T.primaryDark, letterSpacing: '0.04em' }}>
                  ADMIN
                </Typography>
              </Box>
            </Stack>

          </Stack>

          {/* Right: search, notifications, theme toggle, user pill */}
          <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} alignItems="center">
            {/* Theme Toggle - DISABLED FOR ADMIN */}
            {/* <Tooltip title={`Switch to ${isDark ? 'light' : 'dark'} mode`}>
              <IconButton onClick={colorMode.toggleColorMode} aria-label="Toggle theme" size="small" sx={{ width: 36, height: 36, borderRadius: 1, border: `1px solid ${T.border}`, color: T.textMid, bgcolor: T.surface }}>
                {isDark ? <LightMode sx={{ fontSize: 20 }} /> : <DarkMode sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip> */}

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                aria-label="Notifications"
                size="small"
                onClick={handleOpenNotifications}
                sx={{ width: 36, height: 36, borderRadius: 1, border: `1px solid ${T.border}`, color: T.textMid, bgcolor: T.surface }}
              >
                <Badge color="error" variant="dot" overlap="circular" invisible={notifUnreadCount === 0}>
                  <NotifIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={notifAnchorEl}
              open={Boolean(notifAnchorEl)}
              onClose={handleCloseNotifications}
              PaperProps={{
                sx: {
                  width: 360,
                  maxWidth: 'min(100vw - 32px, 360px)',
                  borderRadius: 2,
                  p: 0,
                },
              }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.border}` }}>
                <Typography fontWeight={700}>Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  {notifLoading
                    ? 'Loading recent notifications…'
                    : notifications.length > 0
                      ? `Showing recent ${notifications.length} notifications`
                      : 'No recent notifications'}
                </Typography>
              </Box>
              {notifLoading ? (
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No notifications to show.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                  {notifications.map((notification, idx) => (
                    <Box
                      key={notification._id || notification.id || idx}
                      sx={{ px: 2, py: 1.5, borderBottom: idx < notifications.length - 1 ? `1px solid ${T.border}` : 'none' }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: notification.isRead ? T.textDark : T.primary }}>
                        {notification.title || 'Notification'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'normal' }}>
                        {notification.body || ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  handleCloseNotifications();
                  navigate('/dashboard/marketing?tab=notification');
                }}
                sx={{ justifyContent: 'center' }}
              >
                <Typography color="primary" fontWeight={700}>See more</Typography>
              </MenuItem>
            </Menu>

            {/* User pill */}
            {user && (
              <>
                <Tooltip title="Account options">
                  <Box
                    onClick={e => setAnchorEl(e.currentTarget)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: { xs: 0.5, sm: 1 }, py: 0.5, borderRadius: 1,
                      border: `1px solid ${T.border}`,
                      cursor: 'pointer',
                      bgcolor: T.surface,
                      transition: 'all 0.15s',
                      minHeight: 36,
                      '&:hover': { bgcolor: T.surfaceAlt, borderColor: T.textLight },
                    }}
                  >
                    <Avatar sx={{ width: 28, height: 28, bgcolor: role.color, fontSize: '0.75rem', fontWeight: 700 }}>
                      {initials(user.name)}
                    </Avatar>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: T.textDark, lineHeight: 1.2 }}>
                        {user.name?.split(' ')[0]}
                      </Typography>
                      <Typography variant="caption" sx={{ color: T.textLight, lineHeight: 1 }}>
                        {role.label}
                      </Typography>
                    </Box>
                    <KeyboardArrowDown sx={{ fontSize: 16, color: T.textLight, display: { xs: 'none', sm: 'block' } }} />
                  </Box>
                </Tooltip>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 0.75, minWidth: 220,
                      border: `1px solid ${T.border}`,
                      borderRadius: 1.5,
                      bgcolor: T.surface,
                      boxShadow: '0 16px 40px rgba(16,24,40,0.14)',
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.border}` }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 38, height: 38, bgcolor: role.color, fontSize: '0.875rem', fontWeight: 700 }}>
                        {initials(user.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ color: T.textDark }}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: T.textMid }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <Chip label={role.label} size="small"
                        sx={{ bgcolor: role.bg, color: role.color, fontWeight: 700, fontSize: '0.6875rem', height: 20 }} />
                      {user.gymName && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: T.textMid }}>
                          {user.gymName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <MenuItem
                    component="a"
                    href="/fitistic-staff.apk"
                    download
                    onClick={() => setAnchorEl(null)}
                    sx={{ color: T.primaryDark, fontSize: '0.875rem', py: 1.25, fontWeight: 600, mx: 0.5, mt: 0.5, borderRadius: 1 }}
                  >
                    <DownloadIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Download Staff App
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setAnchorEl(null); localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); }}
                    sx={{ color: T.red, fontSize: '0.875rem', py: 1.25, fontWeight: 600, mx: 0.5, my: 0.5, borderRadius: 1 }}
                  >
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Sign out
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar ── */}
      <Box component="nav" sx={{ width: { sm: DRAWER_W }, flexShrink: { sm: 0 } }}>
        {/* Mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_W, bgcolor: T.surface, borderRight: `1px solid ${T.border}` },
          }}
        >
          <SidebarContent items={items} location={location} navigate={navigate} onClose={() => setMobileOpen(false)} tokens={T} />
        </Drawer>
        {/* Desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_W,
              bgcolor: T.sidebarBg,
              borderRight: `1px solid ${T.sidebarBorder}`,
              top: 64,
              height: 'calc(100% - 64px)',
            },
          }}
          open
        >
          <SidebarContent items={items} location={location} navigate={navigate} tokens={T} />
        </Drawer>
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        role="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${DRAWER_W}px)` },
          minHeight: '100vh',
          bgcolor: T.bg,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', flexShrink: 0 }} />
        <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2.5, lg: 3 }, maxWidth: 1600, width: '100%', mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
